import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { logFeatureView } from '../utils/userEvents';
import { createHierarchy } from '../utils/filterHierarchy';
import { apiFetch } from '../utils/apiClient';
import {
  clearUnsavedRunScanResults,
  loadUnsavedRunScanResults,
  saveUnsavedRunScanResults,
} from '../utils/runScanResultsSession';

// API base URL - configurable via environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const UNIVERSE_LABELS = {
  ALL: 'All indexed tickers (United States + Canada)',
  SP500: 'S&P 500 (United States)',
  NASDAQ100: 'Nasdaq-100 (United States)',
  DOW30: 'Dow 30 (United States)',
  TSX60: 'S&P/TSX 60 (Canada)',
  TSX_COMPOSITE: 'S&P/TSX Composite (Canada)',
};

const SUPPORTED_INDEX_LABELS = [
  'S&P 500',
  'Nasdaq-100',
  'Dow 30',
  'S&P/TSX 60',
  'S&P/TSX Composite',
];

function formatRunScanError(detail) {
  if (typeof detail !== 'string') {
    return detail;
  }

  const outsideUniverseMatch = detail.match(/^Tickers outside selected universe [^:]+:\s*(.+)$/);
  if (!outsideUniverseMatch) {
    return detail;
  }

  const tickers = outsideUniverseMatch[1].trim();
  const subject = tickers.includes(',') ? 'Tickers' : 'Ticker';
  const indexList = `${SUPPORTED_INDEX_LABELS.slice(0, -1).join(', ')}, or ${SUPPORTED_INDEX_LABELS[SUPPORTED_INDEX_LABELS.length - 1]}`;
  return `${subject} ${tickers} wasn’t found in our supported stock coverage. Check that you entered the correct ticker symbol. If it’s correct, it may not be part of any of the indexes we cover: ${indexList}.`;
}

function getUniverseLabel(universeCode) {
  if (!universeCode) {
    return 'Unknown';
  }
  return UNIVERSE_LABELS[universeCode] || universeCode;
}

function hasModernSnapshotProvenance(scanDetails) {
  return scanDetails?.snapshot_universe_code === 'ALL';
}

function RunScan() {
  const [tickers, setTickers] = useState('');
  const [indexCode, setIndexCode] = useState('ALL');
  const [topN, setTopN] = useState(5);
  const [country, setCountry] = useState('');
  const [sector, setSector] = useState('');
  const [industry, setIndustry] = useState('');
  const [results, setResults] = useState(loadUnsavedRunScanResults);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [savedScanId, setSavedScanId] = useState(null);
  const [isSavingScan, setIsSavingScan] = useState(false);
  const [scanName, setScanName] = useState('');

  // Saved scans state
  const [scans, setScans] = useState([]);
  const [loadingScans, setLoadingScans] = useState(true);
  const [selectedScan, setSelectedScan] = useState(null);
  const [scanDetails, setScanDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(true);
  
  // Comparison state
  const [compareScans, setCompareScans] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [comparisonMode, setComparisonMode] = useState('previous');
  const [comparisonNote, setComparisonNote] = useState('');

  // Fetch saved scans on component mount
  useEffect(() => {
    logFeatureView('scans', 'run_scan');
    fetchScans();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchFilterOptions = async () => {
      setLoadingFilterOptions(true);
      try {
        const response = await apiFetch(
          `${API_BASE_URL}/scanner/filter-options?index_code=${encodeURIComponent(indexCode)}`,
          {
            method: 'GET',
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            typeof errorData.detail === 'string'
              ? errorData.detail
              : JSON.stringify(errorData.detail)
          );
        }

        const data = await response.json();
        if (!cancelled) {
          setFilterOptions(data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch Scanner filter options:', err);
          setFilterOptions({ index_code: indexCode, countries: [], sectors: [], industries: [], records: [] });
        }
      } finally {
        if (!cancelled) {
          setLoadingFilterOptions(false);
        }
      }
    };

    fetchFilterOptions();

    return () => {
      cancelled = true;
    };
  }, [indexCode]);

  const hierarchy = useMemo(
    () => createHierarchy(filterOptions?.records || []),
    [filterOptions]
  );
  const hierarchyReady = filterOptions?.index_code === indexCode;

  const countryOptions = useMemo(
    () => Array.isArray(filterOptions?.countries) ? filterOptions.countries : [],
    [filterOptions]
  );

  const sectorOptions = useMemo(
    () => hierarchy.getSectors(indexCode, country || null),
    [hierarchy, indexCode, country]
  );

  const industryOptions = useMemo(
    () => hierarchy.getIndustries(indexCode, country || null, sector || null),
    [hierarchy, indexCode, country, sector]
  );

  useEffect(() => {
    if (!hierarchyReady) {
      return;
    }
    const next = hierarchy.clearDependentFilters({
      universe: indexCode,
      country,
      sector,
      industry,
    });

    if ((next.country || '') !== country) {
      setCountry(next.country || '');
    }
    if ((next.sector || '') !== sector) {
      setSector(next.sector || '');
    }
    if ((next.industry || '') !== industry) {
      setIndustry(next.industry || '');
    }
  }, [hierarchy, hierarchyReady, indexCode, country, sector, industry]);

  const fetchScans = async () => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/scans`, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const data = await response.json();
      setScans(data.scans || []);
    } catch (err) {
      console.error('Failed to fetch scans:', err);
    } finally {
      setLoadingScans(false);
    }
  };

  const handleScanClick = async (scanId) => {
    setSelectedScan(scanId);
    setLoadingDetails(true);
    setScanDetails(null);
    setError('');

    try {
      const response = await apiFetch(`${API_BASE_URL}/scans/${scanId}`, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const data = await response.json();
      setScanDetails(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCompareToggle = (scanId) => {
    setCompareScans(prev => {
      if (prev.includes(scanId)) {
        return prev.filter(id => id !== scanId);
      } else if (prev.length < 2) {
        return [...prev, scanId];
      } else {
        return prev;
      }
    });
  };

  const handleCompareScans = async () => {
    setLoadingComparison(true);
    setComparisonData(null);
    setError('');
    setComparisonNote('');

    try {
      let scanId1, scanId2;
      
      if (comparisonMode === 'manual') {
        if (compareScans.length !== 2) {
          setError('Please select exactly 2 scans to compare');
          setLoadingComparison(false);
          return;
        }
        scanId1 = compareScans[0];
        scanId2 = compareScans[1];
      } else if (comparisonMode === 'previous') {
        const sortedScans = [...scans].sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        
        if (sortedScans.length < 2) {
          setError('Not enough scans available for comparison');
          setLoadingComparison(false);
          return;
        }
        
        const latestScan = sortedScans[0];
        const latestDate = new Date(latestScan.created_at);
        
        let previousScan = null;
        let minTimeDiff = Infinity;
        
        for (let i = 1; i < sortedScans.length; i++) {
          const scanDate = new Date(sortedScans[i].created_at);
          if (scanDate < latestDate) {
            const timeDiff = latestDate - scanDate;
            if (timeDiff < minTimeDiff) {
              minTimeDiff = timeDiff;
              previousScan = sortedScans[i];
            }
          }
        }
        
        if (!previousScan) {
          setError('No previous scan found before the latest scan');
          setLoadingComparison(false);
          return;
        }
        
        scanId1 = previousScan.id;
        scanId2 = latestScan.id;
      } else {
        const sortedScans = [...scans].sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        
        if (sortedScans.length < 2) {
          setError('Not enough scans available for comparison');
          setLoadingComparison(false);
          return;
        }
        
        const latestScan = sortedScans[0];
        const latestDate = new Date(latestScan.created_at);
        
        const targetDaysAgo = comparisonMode === '7days' ? 7 : 30;
        const targetDate = new Date(latestDate);
        targetDate.setDate(targetDate.getDate() - targetDaysAgo);
        
        let closestScan = sortedScans[1];
        let minDiff = Math.abs(new Date(closestScan.created_at) - targetDate);
        
        for (let i = 1; i < sortedScans.length; i++) {
          const scanDate = new Date(sortedScans[i].created_at);
          const diff = Math.abs(scanDate - targetDate);
          if (diff < minDiff) {
            minDiff = diff;
            closestScan = sortedScans[i];
          }
        }
        
        const closestDate = new Date(closestScan.created_at);
        const daysDiff = Math.abs((latestDate - closestDate) / (1000 * 60 * 60 * 24));
        const targetDiff = Math.abs(daysDiff - targetDaysAgo);
        
        if (targetDiff > 1) {
          setComparisonNote(`Note: Using closest available scan from ${Math.round(daysDiff)} days ago (target was ${targetDaysAgo} days).`);
        }
        
        scanId1 = closestScan.id;
        scanId2 = latestScan.id;
      }
      
      const response = await apiFetch(
        `${API_BASE_URL}/scans/compare?scan_id_1=${scanId1}&scan_id_2=${scanId2}`,
        {
          method: 'GET',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const data = await response.json();
      setComparisonData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingComparison(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setValidationError('');
    setSaveMessage('');
    setSavedScanId(null);
    setIsSavingScan(false);
    setLoading(true);
    setResults(null);
    clearUnsavedRunScanResults();

    // Build optional ticker list before making API call
    const tickerList = tickers.trim() 
      ? tickers.split(',').map(t => t.trim()).filter(t => t)
      : null;

    try {
      const requestBody = {
        tickers: tickerList,
        index_code: indexCode,
        top_n: topN,
      };

      // Build filters object with only non-empty values
      const filters = {};
      if (country && country !== '') filters.country = country;
      if (sector && sector !== '') filters.sector = sector;
      if (industry && industry !== '') filters.industry = industry;

      // Only add filters to request if at least one filter is set
      if (Object.keys(filters).length > 0) {
        requestBody.filters = filters;
      }

      const response = await apiFetch(`${API_BASE_URL}/rank-stocks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const detail = typeof errorData.detail === 'string'
          ? errorData.detail
          : JSON.stringify(errorData.detail);
        throw new Error(formatRunScanError(detail));
      }

      const data = await response.json();
      setResults(data);
      saveUnsavedRunScanResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClearResults = () => {
    clearUnsavedRunScanResults();
    setResults(null);
    setSaveMessage('');
    setSavedScanId(null);
    setIsSavingScan(false);
  };

  const handleSaveScan = async () => {
    // Prevent duplicate saves
    if (isSavingScan || savedScanId !== null) {
      return;
    }
    
    setSaveMessage('');
    setError('');
    setIsSavingScan(true);

    try {
      const tickerList = tickers.trim() 
        ? tickers.split(',').map(t => t.trim()).filter(t => t)
        : null;
      const selectedTickers = (results.ranked_stocks || [])
        .map(stock => stock.ticker)
        .filter(Boolean);

      if (!results.ranking_provenance) {
        throw new Error('Cannot save scan without ranking provenance. Please run the scan again.');
      }

      const response = await apiFetch(`${API_BASE_URL}/scans`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tickers: tickerList,
          selected_tickers: selectedTickers,
          ranking_provenance: results.ranking_provenance,
          scan_name: scanName.trim() || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const data = await response.json();
      setSavedScanId(data.scan_id);
      setSaveMessage(`Scan saved successfully! Scan ID: ${data.scan_id}`);
      clearUnsavedRunScanResults();
                  
      // Refresh scans list after saving
      fetchScans();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSavingScan(false);
    }
  };

  return (
    <div className="run-scan-page authenticated-page">
      <div className="run-scan-container authenticated-page-content">
        {/* Page Header */}
        <div className="run-scan-header">
          <div className="run-scan-header-content">
            <h1 className="run-scan-title">Stock Scan</h1>
            <p className="run-scan-subtitle">
              Rank stocks by Investment Score using our proprietary value-investing methodology
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="run-scan-error">
            <svg className="run-scan-error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Scan Mode Explanation */}
        <div className="run-scan-info-card">
          <div className="run-scan-info-header">
            <svg className="run-scan-info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <h2 className="run-scan-info-title">How Scanning Works</h2>
          </div>
          <div className="run-scan-info-content">
            <div className="run-scan-info-mode">
              <div className="run-scan-info-mode-badge run-scan-info-mode-badge-free">Free</div>
              <p className="run-scan-info-mode-text">
                Enter up to 10 specific tickers, score them against the global ALL snapshot, then filter by the selected universe
              </p>
            </div>
            <div className="run-scan-info-mode">
              <div className="run-scan-info-mode-badge run-scan-info-mode-badge-pro">Pro & Premium</div>
              <p className="run-scan-info-mode-text">
                Leave tickers blank to score across all tracked tickers first, then apply the selected universe as an active-membership filter
              </p>
            </div>
          </div>
        </div>

        {/* Main Scan Form */}
        <form onSubmit={handleSubmit} className="run-scan-form">
          <div className="run-scan-form-card">
            <h2 className="run-scan-form-card-title">Scan Configuration</h2>
            
            <div className="run-scan-form-group">
              <label htmlFor="tickers-input" className="run-scan-label">
                Tickers
                <span className="run-scan-label-hint">(comma-separated, or leave blank to use universe selection)</span>
              </label>
              <input 
                id="tickers-input"
                type="text" 
                placeholder="e.g., AAPL, MSFT, GOOGL" 
                value={tickers}
                onChange={(e) => setTickers(e.target.value)}
                className="run-scan-input"
              />
              <p className="run-scan-field-help">
                Free users: Enter up to 10 tickers. Submitted tickers outside the selected universe are filtered out after global scoring.
              </p>
            </div>

            <div className="run-scan-configuration-grid">
            <div className="run-scan-form-group">
              <label htmlFor="country-select" className="run-scan-label">
                Country
              </label>
              <select
                id="country-select"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="run-scan-input"
                disabled={loadingFilterOptions}
              >
                <option value="">All</option>
                {countryOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
              <p className="run-scan-field-help">
                Filter stocks by country
              </p>
            </div>

            <div className="run-scan-form-group">
              <label htmlFor="index-code-select" className="run-scan-label">
                Scan Universe
              </label>
              <select
                id="index-code-select"
                value={indexCode}
                onChange={(e) => setIndexCode(e.target.value)}
                className="run-scan-input"
              >
                <option value="ALL">All indexed tickers (United States + Canada)</option>
                <option value="SP500">S&P 500 (United States)</option>
                <option value="NASDAQ100">Nasdaq-100 (United States)</option>
                <option value="DOW30">Dow 30 (United States)</option>
                <option value="TSX60">S&P/TSX 60 (Canada)</option>
                <option value="TSX_COMPOSITE">S&P/TSX Composite (Canada)</option>
              </select>
            </div>

            <div className="run-scan-form-group">
              <label htmlFor="sector-select" className="run-scan-label">
                Sector
              </label>
              <select
                id="sector-select"
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                className="run-scan-input"
                disabled={loadingFilterOptions}
              >
                <option value="">All</option>
                {sectorOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
              <p className="run-scan-field-help">
                Filter stocks by sector
              </p>
            </div>

            <div className="run-scan-form-group">
              <label htmlFor="industry-select" className="run-scan-label">
                Industry
              </label>
              <select
                id="industry-select"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="run-scan-input"
                disabled={loadingFilterOptions}
              >
                <option value="">All</option>
                {industryOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
              <p className="run-scan-field-help">
                Filter stocks by industry
              </p>
            </div>

            <div className="run-scan-form-group">
              <label htmlFor="top-n-input" className="run-scan-label">
                Top N Stocks
                <span className="run-scan-label-required">*</span>
              </label>
              <input 
                id="top-n-input"
                type="number" 
                value={topN}
                onChange={(e) => setTopN(parseInt(e.target.value))}
                required
                min="1"
                className="run-scan-input run-scan-input-number"
              />
              <p className="run-scan-field-help">
                Number of top-ranked stocks to return in results
              </p>
            </div>

            </div>

            <div className="run-scan-form-actions">
              <button type="submit" disabled={loading} className="run-scan-btn-primary">
                {loading ? (
                  <>
                    <svg className="run-scan-btn-spinner" viewBox="0 0 24 24">
                      <circle className="run-scan-spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3"></circle>
                    </svg>
                    Running Scan...
                  </>
                ) : (
                  <>
                    <svg className="run-scan-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                    Run Scan
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Validation Warning Message - Above Results */}
        {validationError && (
          <div className="run-scan-warning">
            <svg className="run-scan-warning-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <span>{validationError}</span>
          </div>
        )}

        {/* Results Section */}
        <div className="run-scan-results-section">
          <div className="run-scan-results-header">
            <h2 className="run-scan-results-title">Scan Results</h2>
            {results && results.ranked_stocks && results.ranked_stocks.length > 0 && (
              <div className="run-scan-results-actions">
                <input
                  type="text"
                  placeholder="Scan name (optional)"
                  value={scanName}
                  onChange={(e) => setScanName(e.target.value)}
                  maxLength={120}
                  className="run-scan-input"
                  style={{ width: '200px', marginRight: '10px' }}
                />
                <button 
                  type="button" 
                  onClick={handleSaveScan} 
                  disabled={isSavingScan || savedScanId !== null}
                  className="run-scan-btn-save"
                >
                  {isSavingScan ? (
                    <>
                      <svg className="run-scan-btn-spinner" viewBox="0 0 24 24">
                        <circle className="run-scan-spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3"></circle>
                      </svg>
                      Saving...
                    </>
                  ) : savedScanId !== null ? (
                    <>
                      <svg className="run-scan-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Saved
                    </>
                  ) : (
                    <>
                      <svg className="run-scan-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                        <polyline points="7 3 7 8 15 8"></polyline>
                      </svg>
                      Save Scan
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClearResults}
                  className="run-scan-btn-save"
                >
                  Clear Results
                </button>
              </div>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="run-scan-loading">
              <svg className="run-scan-loading-spinner" viewBox="0 0 24 24">
                <circle className="run-scan-spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3"></circle>
              </svg>
              <p className="run-scan-loading-text">Analyzing stocks and calculating Investment Scores...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && !results && (
            <div className="run-scan-empty">
              <svg className="run-scan-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <p className="run-scan-empty-text">Configure your scan parameters and click "Run Scan" to see results</p>
            </div>
          )}

          {/* No Results */}
          {results && results.ranked_stocks && results.ranked_stocks.length === 0 && (
            <div className="run-scan-no-results">
              <svg className="run-scan-no-results-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              <p className="run-scan-no-results-text">No stocks matched your criteria. Try adjusting your filters.</p>
            </div>
          )}

          {/* Results Table */}
          {results && results.ranked_stocks && results.ranked_stocks.length > 0 && (
            <>
              {saveMessage && (
                <div className="run-scan-success">
                  <svg className="run-scan-success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span>Scan saved successfully!</span>
                </div>
              )}
              
              <div className="run-scan-table-wrapper run-scan-live-results-table-wrapper">
                <table className="run-scan-table run-scan-live-results-table">
                  <colgroup>
                    <col className="run-scan-live-col-ticker" />
                    <col className="run-scan-live-col-rank" />
                    <col className="run-scan-live-col-sector" />
                    <col className="run-scan-live-col-industry" />
                    <col className="run-scan-live-col-investment" />
                    <col className="run-scan-live-col-valuation" />
                    <col className="run-scan-live-col-profitability" />
                    <col className="run-scan-live-col-balance" />
                    <col className="run-scan-live-col-growth" />
                    <col className="run-scan-live-col-risk" />
                    <col className="run-scan-live-col-return" />
                  </colgroup>
                  <thead className="run-scan-table-header">
                    <tr>
                      <th className="run-scan-th run-scan-th-ticker">Ticker</th>
                      <th className="run-scan-th run-scan-th-rank">Global Rank</th>
                      <th className="run-scan-th run-scan-th-sector">Sector</th>
                      <th className="run-scan-th run-scan-th-industry">Industry</th>
                      <th className="run-scan-th run-scan-th-investment">Investment Score</th>
                      <th className="run-scan-th run-scan-th-score">Valuation</th>
                      <th className="run-scan-th run-scan-th-score">Profitability</th>
                      <th className="run-scan-th run-scan-th-score">Balance Sheet</th>
                      <th className="run-scan-th run-scan-th-score">Growth/CF</th>
                      <th className="run-scan-th run-scan-th-score">Risk/Stability</th>
                      <th className="run-scan-th run-scan-th-score">Return Consistency</th>
                    </tr>
                  </thead>
                  <tbody className="run-scan-table-body">
                    {results.ranked_stocks.map((stock, index) => (
                      <tr key={index} className={`run-scan-tr ${index === 0 ? 'run-scan-tr-top' : ''}`}>
                        <td className="run-scan-td run-scan-td-ticker">
                          <Link 
                            to={`/stock/${stock.ticker}`}
                            className="run-scan-ticker-link"
                            title="View stock research page"
                          >
                            {stock.ticker}
                            <svg className="run-scan-ticker-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="11" cy="11" r="8"></circle>
                              <path d="m21 21-4.35-4.35"></path>
                            </svg>
                          </Link>
                        </td>
                        <td className="run-scan-td run-scan-td-rank">
                          {stock.rank}
                        </td>
                        <td className="run-scan-td run-scan-td-sector">{stock.sector || 'N/A'}</td>
                        <td className="run-scan-td run-scan-td-industry">{stock.industry || 'N/A'}</td>
                        <td className="run-scan-td run-scan-td-investment">
                          {stock.investment_score !== null && stock.investment_score !== undefined && !isNaN(stock.investment_score)
                            ? stock.investment_score.toFixed(1)
                            : 'N/A'}
                        </td>
                        <td className="run-scan-td run-scan-td-score">
                          {stock.valuation_score !== null && stock.valuation_score !== undefined && !isNaN(stock.valuation_score) 
                            ? stock.valuation_score.toFixed(1) 
                            : 'N/A'}
                        </td>
                        <td className="run-scan-td run-scan-td-score">
                          {stock.profitability_score !== null && stock.profitability_score !== undefined && !isNaN(stock.profitability_score) 
                            ? stock.profitability_score.toFixed(1) 
                            : 'N/A'}
                        </td>
                        <td className="run-scan-td run-scan-td-score">
                          {stock.balance_sheet_score !== null && stock.balance_sheet_score !== undefined && !isNaN(stock.balance_sheet_score) 
                            ? stock.balance_sheet_score.toFixed(1) 
                            : 'N/A'}
                        </td>
                        <td className="run-scan-td run-scan-td-score">
                          {stock.growth_cashflow_score !== null && stock.growth_cashflow_score !== undefined && !isNaN(stock.growth_cashflow_score) 
                            ? stock.growth_cashflow_score.toFixed(1) 
                            : 'N/A'}
                        </td>
                        <td className="run-scan-td run-scan-td-score">
                          {stock.risk_stability_score !== null && stock.risk_stability_score !== undefined && !isNaN(stock.risk_stability_score) 
                            ? stock.risk_stability_score.toFixed(1) 
                            : 'N/A'}
                        </td>
                        <td className="run-scan-td run-scan-td-score">
                          {stock.return_consistency_score !== null && stock.return_consistency_score !== undefined && !isNaN(stock.return_consistency_score) 
                            ? stock.return_consistency_score.toFixed(1) 
                            : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Saved Scans Section */}
        <div className="run-scan-saved-section">
          <div className="run-scan-saved-header">
            <h2 className="run-scan-saved-title">Your Saved Scans</h2>
            <p className="run-scan-saved-subtitle">
              View and compare your scan history to track ranking changes over time
            </p>
          </div>

          {/* Comparison Mode Selector */}
          {scans.length >= 2 && (
            <div className="run-scan-comparison-mode">
              <div className="run-scan-comparison-mode-header">
                <strong>Comparison Mode:</strong>
              </div>
              <div className="run-scan-comparison-mode-options">
                <label className="run-scan-comparison-mode-option">
                  <input 
                    type="radio" 
                    value="manual" 
                    checked={comparisonMode === 'manual'}
                    onChange={(e) => {
                      setComparisonMode(e.target.value);
                      setCompareScans([]);
                      setComparisonData(null);
                    }}
                  />
                  <span>Manual</span>
                </label>
                <label className="run-scan-comparison-mode-option">
                  <input 
                    type="radio" 
                    value="previous" 
                    checked={comparisonMode === 'previous'}
                    onChange={(e) => {
                      setComparisonMode(e.target.value);
                      setCompareScans([]);
                      setComparisonData(null);
                    }}
                  />
                  <span>Previous scan</span>
                </label>
                <label className="run-scan-comparison-mode-option">
                  <input 
                    type="radio" 
                    value="7days" 
                    checked={comparisonMode === '7days'}
                    onChange={(e) => {
                      setComparisonMode(e.target.value);
                      setCompareScans([]);
                      setComparisonData(null);
                    }}
                  />
                  <span>7 days ago</span>
                </label>
                <label className="run-scan-comparison-mode-option">
                  <input 
                    type="radio" 
                    value="30days" 
                    checked={comparisonMode === '30days'}
                    onChange={(e) => {
                      setComparisonMode(e.target.value);
                      setCompareScans([]);
                      setComparisonData(null);
                    }}
                  />
                  <span>30 days ago</span>
                </label>
              </div>
            </div>
          )}

          {/* Scans List */}
          {loadingScans && <p className="run-scan-saved-loading">Loading saved scans...</p>}
          
          {!loadingScans && scans.length === 0 && (
            <div className="run-scan-saved-empty">
              <p>No saved scans yet. Run and save a scan to see it here!</p>
            </div>
          )}
          
          {!loadingScans && scans.length > 0 && (
            <div className="run-scan-saved-list-container">
              <ul className="run-scan-saved-list">
                {scans.map((scan, index) => {
                  const userScanNumber = scans.length - index;
                  const displayName = scan.scan_name || `Saved Scan #${userScanNumber}`;
                  return (
                    <li key={scan.id} className="run-scan-saved-item">
                      {comparisonMode === 'manual' && (
                        <input 
                          type="checkbox"
                          checked={compareScans.includes(scan.id)}
                          onChange={() => handleCompareToggle(scan.id)}
                          disabled={!compareScans.includes(scan.id) && compareScans.length >= 2}
                          className="run-scan-saved-checkbox"
                        />
                      )}
                      <a 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          handleScanClick(scan.id);
                        }}
                        className={`run-scan-saved-link ${selectedScan === scan.id ? 'active' : ''}`}
                      >
                        {displayName}
                      </a>
                      <span className="run-scan-saved-date">
                        {new Date(scan.created_at).toLocaleString()}
                      </span>
                    </li>
                  );
                })}
              </ul>

              {/* Compare Button */}
              {comparisonMode === 'manual' && compareScans.length === 2 && (
                <button 
                  onClick={handleCompareScans}
                  className="run-scan-btn-compare"
                >
                  Compare Selected Scans
                </button>
              )}
              {comparisonMode === 'manual' && compareScans.length !== 2 && (
                <p className="run-scan-comparison-hint">
                  Select exactly 2 scans to compare
                </p>
              )}
              {comparisonMode === 'previous' && scans.length >= 2 && (
                <button 
                  onClick={handleCompareScans}
                  className="run-scan-btn-compare"
                >
                  Compare Latest with Previous Scan
                </button>
              )}
              {comparisonMode === 'previous' && scans.length < 2 && (
                <p className="run-scan-comparison-hint">
                  Need at least 2 scans for comparison
                </p>
              )}
              {(comparisonMode === '7days' || comparisonMode === '30days') && scans.length >= 2 && (
                <button 
                  onClick={handleCompareScans}
                  className="run-scan-btn-compare"
                >
                  Compare Latest with {comparisonMode === '7days' ? '7 Days Ago' : '30 Days Ago'}
                </button>
              )}
              {(comparisonMode === '7days' || comparisonMode === '30days') && scans.length < 2 && (
                <p className="run-scan-comparison-hint">
                  Need at least 2 scans for automatic comparison
                </p>
              )}
            </div>
          )}

          {/* Scan Details */}
          {loadingDetails && <p className="run-scan-saved-loading">Loading scan details...</p>}
          
          {scanDetails && (
            <div className="run-scan-detail-card">
              <h3 className="run-scan-detail-title">
                {scanDetails.scan_name || `Scan Details (Saved Scan #${scans.length - scans.findIndex(s => s.id === selectedScan)})`}
              </h3>
              <p className="run-scan-detail-meta">
                <strong>Created:</strong> {new Date(scanDetails.created_at).toLocaleString()}
              </p>
              <p className="run-scan-detail-meta">
                <strong>Tickers:</strong> {
                  scanDetails.tickers 
                    ? (Array.isArray(scanDetails.tickers) 
                        ? scanDetails.tickers.join(', ') 
                        : scanDetails.tickers.split(',').map(t => t.trim()).join(', '))
                    : 'N/A'
                }
              </p>

              {hasModernSnapshotProvenance(scanDetails) ? (
                <>
                  <p className="run-scan-detail-meta">
                    <strong>Scoring Snapshot Universe:</strong> {getUniverseLabel(scanDetails.snapshot_universe_code)} ({scanDetails.snapshot_universe_code || 'N/A'})
                  </p>
                  <p className="run-scan-detail-meta">
                    <strong>Selected Universe Filter:</strong> {getUniverseLabel(scanDetails.selected_universe_code || scanDetails.index_code)} ({scanDetails.selected_universe_code || scanDetails.index_code || 'N/A'})
                  </p>
                </>
              ) : (
                <p className="run-scan-detail-meta">
                  <strong>Snapshot Provenance:</strong> Legacy saved scan - snapshot provenance unavailable
                </p>
              )}

              {scanDetails.results && scanDetails.results.length > 0 && (
                <div className="run-scan-detail-results">
                  <h4 className="run-scan-detail-results-title">Results:</h4>
                  <div className="run-scan-table-wrapper">
                    <table className="run-scan-table">
                      <thead className="run-scan-table-header">
                        <tr>
                          <th className="run-scan-th run-scan-th-rank">Global Rank</th>
                          <th className="run-scan-th run-scan-th-ticker">Ticker</th>
                          <th className="run-scan-th run-scan-th-sector">Sector</th>
                          <th className="run-scan-th run-scan-th-score">Valuation</th>
                          <th className="run-scan-th run-scan-th-score">Profitability</th>
                          <th className="run-scan-th run-scan-th-score">Balance Sheet</th>
                          <th className="run-scan-th run-scan-th-score">Growth/CF</th>
                          <th className="run-scan-th run-scan-th-score">Risk/Stability</th>
                          <th className="run-scan-th run-scan-th-score">Return Consistency</th>
                          <th className="run-scan-th run-scan-th-investment">Investment Score</th>
                        </tr>
                      </thead>
                      <tbody className="run-scan-table-body">
                        {scanDetails.results.map((stock, index) => (
                          <tr key={index} className="run-scan-tr">
                            <td className="run-scan-td run-scan-td-rank">{stock.rank}</td>
                            <td className="run-scan-td run-scan-td-ticker">
                              <Link 
                                to={`/stock/${stock.ticker}`}
                                className="run-scan-ticker-link"
                                title="View stock research page"
                              >
                                {stock.ticker}
                                <svg className="run-scan-ticker-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="11" cy="11" r="8"></circle>
                                  <path d="m21 21-4.35-4.35"></path>
                                </svg>
                              </Link>
                            </td>
                            <td className="run-scan-td run-scan-td-sector">{stock.sector || 'N/A'}</td>
                            <td className="run-scan-td run-scan-td-score">
                              {stock.valuation_score !== null && stock.valuation_score !== undefined && !isNaN(stock.valuation_score) 
                                ? stock.valuation_score.toFixed(1) 
                                : 'N/A'}
                            </td>
                            <td className="run-scan-td run-scan-td-score">
                              {stock.profitability_score !== null && stock.profitability_score !== undefined && !isNaN(stock.profitability_score) 
                                ? stock.profitability_score.toFixed(1) 
                                : 'N/A'}
                            </td>
                            <td className="run-scan-td run-scan-td-score">
                              {stock.balance_sheet_score !== null && stock.balance_sheet_score !== undefined && !isNaN(stock.balance_sheet_score) 
                                ? stock.balance_sheet_score.toFixed(1) 
                                : 'N/A'}
                            </td>
                            <td className="run-scan-td run-scan-td-score">
                              {stock.growth_cashflow_score !== null && stock.growth_cashflow_score !== undefined && !isNaN(stock.growth_cashflow_score) 
                                ? stock.growth_cashflow_score.toFixed(1) 
                                : 'N/A'}
                            </td>
                            <td className="run-scan-td run-scan-td-score">
                              {stock.risk_stability_score !== null && stock.risk_stability_score !== undefined && !isNaN(stock.risk_stability_score) 
                                ? stock.risk_stability_score.toFixed(1) 
                                : 'N/A'}
                            </td>
                            <td className="run-scan-td run-scan-td-score">
                              {stock.return_consistency_score !== null && stock.return_consistency_score !== undefined && !isNaN(stock.return_consistency_score) 
                                ? stock.return_consistency_score.toFixed(1) 
                                : 'N/A'}
                            </td>
                            <td className="run-scan-td run-scan-td-investment">
                              {stock.investment_score !== null && stock.investment_score !== undefined && !isNaN(stock.investment_score) 
                                ? stock.investment_score.toFixed(1) 
                                : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {scanDetails.results && scanDetails.results.length === 0 && (
                <p className="run-scan-detail-no-results">No results found for this scan.</p>
              )}
            </div>
          )}

          {/* Comparison Results */}
          {loadingComparison && <p className="run-scan-saved-loading">Loading comparison...</p>}
          
          {comparisonData && (
            <div className="run-scan-comparison-card">
              <h3 className="run-scan-comparison-title">Scan Comparison</h3>
              
              {comparisonNote && (
                <p className="run-scan-comparison-note">
                  ℹ️ {comparisonNote}
                </p>
              )}
              
              {(() => {
                const scan1 = scans.find(s => s.id === comparisonData.scan_id_1);
                const scan2 = scans.find(s => s.id === comparisonData.scan_id_2);
                if (scan1 && scan2) {
                  const date1 = new Date(scan1.created_at);
                  const date2 = new Date(scan2.created_at);
                  
                  const isLatest1 = date1 > date2;
                  const latestScan = isLatest1 ? scan1 : scan2;
                  const comparisonScan = isLatest1 ? scan2 : scan1;
                  
                  const latestScanNumber = scans.length - scans.findIndex(s => s.id === latestScan.id);
                  const comparisonScanNumber = scans.length - scans.findIndex(s => s.id === comparisonScan.id);
                  
                  const diffMs = Math.abs(date2 - date1);
                  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                  
                  let timeDiffText = '';
                  if (diffDays > 0) {
                    timeDiffText = `${diffDays} day${diffDays > 1 ? 's' : ''}`;
                    if (diffHours > 0) timeDiffText += `, ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
                  } else if (diffHours > 0) {
                    timeDiffText = `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
                    if (diffMinutes > 0) timeDiffText += `, ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
                  } else {
                    timeDiffText = `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
                  }
                  
                  return (
                    <div className="run-scan-comparison-meta">
                      <p>
                        <strong>Latest Scan:</strong> Saved Scan #{latestScanNumber} ({new Date(latestScan.created_at).toLocaleString()})
                      </p>
                      <p>
                        <strong>Comparison Scan:</strong> Saved Scan #{comparisonScanNumber} ({new Date(comparisonScan.created_at).toLocaleString()})
                      </p>
                      <p className="run-scan-comparison-time-diff">
                        <strong>Time between scans:</strong> {timeDiffText}
                      </p>
                    </div>
                  );
                }
                return (
                  <p className="run-scan-comparison-meta">
                    <strong>Scan 1:</strong> {comparisonData.scan_id_1} | <strong>Scan 2:</strong> {comparisonData.scan_id_2}
                  </p>
                );
              })()}

              {comparisonData.top_gainers && comparisonData.top_gainers.length > 0 && (
                <div className="run-scan-comparison-section">
                  <h4 className="run-scan-comparison-section-title">Top Gainers:</h4>
                  <div className="run-scan-table-wrapper">
                    <table className="run-scan-table">
                      <thead className="run-scan-table-header">
                        <tr>
                          <th className="run-scan-th run-scan-th-ticker">Ticker</th>
                          <th className="run-scan-th run-scan-th-score">Global Rank Change</th>
                          <th className="run-scan-th run-scan-th-score">Investment Score Change</th>
                        </tr>
                      </thead>
                      <tbody className="run-scan-table-body">
                        {comparisonData.top_gainers.map((item, index) => {
                          const scoreChange = item.investment_score_change;
                          return (
                            <tr key={index} className="run-scan-tr">
                              <td className="run-scan-td run-scan-td-ticker">
                                <Link
                                  to={`/stock/${item.ticker}`}
                                  className="run-scan-ticker-link"
                                  title="View stock research page"
                                >
                                  {item.ticker}
                                  <svg className="run-scan-ticker-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <path d="m21 21-4.35-4.35"></path>
                                  </svg>
                                </Link>
                              </td>
                              <td className="run-scan-td run-scan-td-score theme-success" style={{ fontWeight: 'bold' }}>
                                ↑ {item.rank_change}
                              </td>
                              <td className={`run-scan-td run-scan-td-score ${scoreChange && scoreChange > 0 ? 'theme-success' : scoreChange && scoreChange < 0 ? 'theme-error' : 'theme-text-muted'}`} style={{
                                fontWeight: scoreChange ? 'bold' : 'normal'
                              }}>
                                {scoreChange !== null && scoreChange !== undefined 
                                  ? `${scoreChange > 0 ? '+' : ''}${scoreChange.toFixed(1)}`
                                  : 'N/A'
                                }
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {comparisonData.top_losers && comparisonData.top_losers.length > 0 && (
                <div className="run-scan-comparison-section">
                  <h4 className="run-scan-comparison-section-title">Top Losers:</h4>
                  <div className="run-scan-table-wrapper">
                    <table className="run-scan-table">
                      <thead className="run-scan-table-header">
                        <tr>
                          <th className="run-scan-th run-scan-th-ticker">Ticker</th>
                          <th className="run-scan-th run-scan-th-score">Global Rank Change</th>
                          <th className="run-scan-th run-scan-th-score">Investment Score Change</th>
                        </tr>
                      </thead>
                      <tbody className="run-scan-table-body">
                        {comparisonData.top_losers.map((item, index) => {
                          const scoreChange = item.investment_score_change;
                          return (
                            <tr key={index} className="run-scan-tr">
                              <td className="run-scan-td run-scan-td-ticker">
                                <Link
                                  to={`/stock/${item.ticker}`}
                                  className="run-scan-ticker-link"
                                  title="View stock research page"
                                >
                                  {item.ticker}
                                  <svg className="run-scan-ticker-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <path d="m21 21-4.35-4.35"></path>
                                  </svg>
                                </Link>
                              </td>
                              <td className="run-scan-td run-scan-td-score theme-error" style={{ fontWeight: 'bold' }}>
                                ↓ {Math.abs(item.rank_change)}
                              </td>
                              <td className={`run-scan-td run-scan-td-score ${scoreChange && scoreChange > 0 ? 'theme-success' : scoreChange && scoreChange < 0 ? 'theme-error' : 'theme-text-muted'}`} style={{
                                fontWeight: scoreChange ? 'bold' : 'normal'
                              }}>
                                {scoreChange !== null && scoreChange !== undefined 
                                  ? `${scoreChange > 0 ? '+' : ''}${scoreChange.toFixed(1)}`
                                  : 'N/A'
                                }
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {comparisonData.shared_tickers && comparisonData.shared_tickers.length > 0 && (
                <div className="run-scan-comparison-section">
                  <h4 className="run-scan-comparison-section-title">Shared Tickers - Rank Changes:</h4>
                  <div className="run-scan-table-wrapper">
                    <table className="run-scan-table">
                      <thead className="run-scan-table-header">
                        <tr>
                          <th className="run-scan-th run-scan-th-ticker">Ticker</th>
                          <th className="run-scan-th run-scan-th-score">Scan 1 Global Rank</th>
                          <th className="run-scan-th run-scan-th-score">Scan 2 Global Rank</th>
                          <th className="run-scan-th run-scan-th-score">Global Rank Change</th>
                          <th className="run-scan-th run-scan-th-score">Investment Score Change</th>
                        </tr>
                      </thead>
                      <tbody className="run-scan-table-body">
                        {comparisonData.shared_tickers.map((item, index) => {
                          const rankChange = item.rank_change;
                          const changeToneClass = rankChange > 0 ? 'theme-success' : rankChange < 0 ? 'theme-error' : 'theme-text-muted';
                          const changeSymbol = rankChange > 0 ? '↑' : rankChange < 0 ? '↓' : '';
                          const scoreChange = item.investment_score_change;
                          return (
                            <tr key={index} className="run-scan-tr">
                              <td className="run-scan-td run-scan-td-ticker">
                                <Link
                                  to={`/stock/${item.ticker}`}
                                  className="run-scan-ticker-link"
                                  title="View stock research page"
                                >
                                  {item.ticker}
                                  <svg className="run-scan-ticker-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <path d="m21 21-4.35-4.35"></path>
                                  </svg>
                                </Link>
                              </td>
                              <td className="run-scan-td run-scan-td-score">{item.old_rank}</td>
                              <td className="run-scan-td run-scan-td-score">{item.new_rank}</td>
                              <td className={`run-scan-td run-scan-td-score ${changeToneClass}`} style={{
                                fontWeight: 'bold'
                              }}>
                                {changeSymbol} {Math.abs(rankChange)}
                              </td>
                              <td className={`run-scan-td run-scan-td-score ${scoreChange && scoreChange > 0 ? 'theme-success' : scoreChange && scoreChange < 0 ? 'theme-error' : 'theme-text-muted'}`} style={{
                                fontWeight: scoreChange ? 'bold' : 'normal'
                              }}>
                                {scoreChange !== null && scoreChange !== undefined 
                                  ? `${scoreChange > 0 ? '+' : ''}${scoreChange.toFixed(1)}`
                                  : 'N/A'
                                }
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {comparisonData.shared_tickers && comparisonData.shared_tickers.length === 0 && (
                <p className="run-scan-comparison-no-shared">No shared tickers between these scans.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RunScan;
